/**
 * "Leer dit" (sessie 84): JP leert Nova en Sanne iets vanuit de copiloot.
 *
 * - `isLeerDit` herkent de opdracht aan het begin van wat JP typt of
 *   inspreekt ("leer dit ...", "leer het ...", "leer: ...", "onthoud
 *   voortaan ..."), ook met de komma's en dubbele punten die spraak-naar-
 *   tekst ertussen zet.
 * - `leerDitApi` praat met `/api/werkplek/leer-dit` (contract sessie 84).
 * - `useLes` houdt één les bij en vraagt hem elke 2 seconden opnieuw op
 *   zolang de herspeling nog loopt; bij klaar, mislukt, overgeslagen, een
 *   afgewezen les of unmount stopt dat.
 * - `useLessenVoorContext` haalt de open en recent vastgezette lessen van
 *   de pagina of zaak op, zodat een open voorstel na herladen terug is.
 *
 * Typen staan hier en niet in `@/types/api`, net als bij `copilootApi`:
 * zo blijft dit pakket binnen zijn eigen bestanden.
 */
import { useQuery } from "@tanstack/react-query";

import { apiClient } from "@/lib/api";

/* ── Herkenner ──────────────────────────────────────────────────── */

/**
 * Begint met "leer dit", "leer het", "leer:" of "onthoud voortaan".
 * Tussen de woorden mag een komma, punt of dubbele punt staan ("Leer,
 * dit: ..."), en ervoor mag witruimte staan. "Leerdoelen" of "leer hem
 * hoe ..." tellen niet.
 */
const LEER_DIT =
  /^\s*(?:leer\s*[,.:]?\s*(?:dit|het)(?![\p{L}\p{N}])|leer\s*:|onthoud\s*[,.:]?\s*voortaan(?![\p{L}\p{N}]))/iu;

export function isLeerDit(tekst: string): boolean {
  return LEER_DIT.test(tekst);
}

/* ── Typen (API-contract sessie 84) ─────────────────────────────── */

export type LesStatus =
  | "voorstel"
  | "geweigerd"
  | "vastgezet"
  | "afgewezen"
  | "teruggedraaid"
  | "bezig"
  | "mislukt"
  | "vervangen";

export type LesSoort = "regel" | "feit";

export type BotsingKeuze = "vervang" | "naast";

export interface LesBron {
  titel: string;
  url: string;
}

export interface LesVangrail {
  sleutel: string;
  geslaagd: boolean;
  uitleg: string;
}

export interface LesBotsing {
  id: string;
  met: "regel" | "kaart" | "gedragsdocument";
  ref_id: string | null;
  titel: string;
  tekst: string;
  uitleg: string;
  keuzes: string[];
}

export interface HerspeelZaak {
  zaak_id: string;
  label: string;
  kanaal: string;
  vraag_kort: string;
  /** Leeg als er geen antwoord kwam; dan staat de reden in `fout`. */
  oud: string | null;
  nieuw: string | null;
  fout: string | null;
  is_huidige: boolean;
}

export interface LesHerspeling {
  status: "bezig" | "klaar" | "mislukt" | "overgeslagen";
  reden: string | null;
  zaken: HerspeelZaak[];
}

export interface Les {
  id: string;
  status: LesStatus;
  soort: LesSoort;
  invoer: string;
  titel: string;
  tekst: string;
  onderbouwing: string | null;
  bronnen: LesBron[];
  online_nagezocht: boolean;
  waarschuwingen: string[];
  vraagtypen: string[];
  kanalen: string[];
  vangrails: LesVangrail[];
  geweigerd_reden: string | null;
  botsingen: LesBotsing[];
  herspeling: LesHerspeling;
  kosten_eur: number;
  mag_vastzetten: boolean;
  reden_niet_vastzetten: string | null;
  /** Mag dit account Niet (afwijzen) en Terugdraaien? Zo niet: de reden. */
  mag_afwijzen: boolean;
  mag_terugdraaien: boolean;
  reden_niet_wijzigen: string | null;
  voorstel_id: string | null;
  kaart_id: string | null;
}

/** Waar JP was toen hij de les gaf. Het id is altijd tekst of null:
 *  een zaak-uuid, maar ook een paginanaam als "kennis" of "vandaag". */
export interface LesContext {
  soort: string;
  id: string | null;
}

/** Maak er altijd `string | null` van, nooit undefined of een getal. */
export function lesContext(soort: string, id: unknown): LesContext {
  return { soort, id: id === null || id === undefined || id === "" ? null : String(id) };
}

/* ── Invoerlimiet ───────────────────────────────────────────────── */

/** Zo lang mag een les zijn; de server weigert langer (422). */
export const MAX_LES_TEKENS = 2000;
/** Vanaf hier staat de teller in beeld. */
export const LES_TELLER_VANAF = 1800;

export function lesTeLang(tekst: string): boolean {
  return tekst.trim().length > MAX_LES_TEKENS;
}

/* ── API ────────────────────────────────────────────────────────── */

const PAD = "/api/werkplek/leer-dit";

/**
 * Uitschrijven, vangrails toetsen en botsingen zoeken duurt langer dan
 * de gewone 30 s van de apiClient. Alleen voor deze ene aanroep.
 */
export const MAAK_TIMEOUT_MS = 120_000;

export const leerDitApi = {
  async maak(tekst: string, context: LesContext): Promise<Les> {
    const { data } = await apiClient.post<Les>(
      PAD,
      { tekst, context: lesContext(context.soort, context.id) },
      { timeout: MAAK_TIMEOUT_MS },
    );
    return data;
  },
  /** Open en recent vastgezette lessen van één pagina of zaak. */
  async lijst(context: LesContext): Promise<Les[]> {
    const params: Record<string, string> = { context_soort: context.soort };
    if (context.id !== null) params.context_id = context.id;
    const { data } = await apiClient.get<Les[] | { lessen?: Les[] }>(PAD, { params });
    if (Array.isArray(data)) return data;
    return data?.lessen ?? [];
  },
  async haal(id: string): Promise<Les> {
    const { data } = await apiClient.get<Les>(`${PAD}/${id}`);
    return data;
  },
  async vastzetten(id: string, keuzes: Record<string, BotsingKeuze>): Promise<Les> {
    const { data } = await apiClient.post<Les>(`${PAD}/${id}/vastzetten`, { keuzes });
    return data;
  },
  async terugdraaien(id: string): Promise<Les> {
    const { data } = await apiClient.post<Les>(`${PAD}/${id}/terugdraaien`);
    return data;
  },
  async afwijzen(id: string): Promise<Les> {
    const { data } = await apiClient.post<Les>(`${PAD}/${id}/afwijzen`);
    return data;
  },
};

export function lesSleutel(id: string): string[] {
  return ["leer-dit", id];
}

export function lessenSleutel(context: LesContext): (string | null)[] {
  return ["leer-dit", "lijst", context.soort, context.id];
}

/** Zolang de herspeling loopt: elke 2 seconden opnieuw vragen. */
export const POLL_MS = 2000;

/** Een afgewezen, geweigerde of teruggedraaide les wacht nergens meer op. */
const KLAAR_STATUS: ReadonlySet<LesStatus> = new Set([
  "afgewezen",
  "geweigerd",
  "teruggedraaid",
  "mislukt",
  "vervangen",
]);

/** Loopt het voorbeeld nog, en doet dat er voor deze les nog toe? */
export function herspelingLoopt(les: Les | undefined): boolean {
  if (!les) return false;
  return les.herspeling?.status === "bezig" && !KLAAR_STATUS.has(les.status);
}

/**
 * Eén les, met polling zolang `herspeling.status` "bezig" is en de les
 * nog open of vastgezet is. De eerste stand komt uit de POST, dus er is
 * nooit een lege kaart. Unmount stopt het pollen vanzelf.
 */
export function useLes(begin: Les) {
  return useQuery({
    queryKey: lesSleutel(begin.id),
    queryFn: () => leerDitApi.haal(begin.id),
    initialData: begin,
    // De eerste stand is net binnen; niet meteen opnieuw ophalen.
    staleTime: POLL_MS,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => (herspelingLoopt(query.state.data) ? POLL_MS : false),
  });
}

/**
 * De lessen die bij deze pagina of zaak horen, bij openen van de
 * copiloot. Uit als er geen context is.
 */
export function useLessenVoorContext(context: LesContext | null) {
  const ctx = context ? lesContext(context.soort, context.id) : null;
  return useQuery({
    queryKey: ctx ? lessenSleutel(ctx) : ["leer-dit", "lijst", "geen"],
    queryFn: () => leerDitApi.lijst(ctx as LesContext),
    enabled: ctx !== null,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/* ── Fouten in gewone taal ──────────────────────────────────────── */

/**
 * Een eigen melding van de server (`detail`) tonen we letterlijk; een
 * kale technische fout nooit.
 */
export function lesFout(e: unknown, wat: string): string {
  const antwoord = (e as { response?: { status?: number; data?: { detail?: unknown } } })
    ?.response;
  const detail = antwoord?.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return `${wat}: ${detail.trim().replace(/\.?$/, ".")}`;
  }
  const status = antwoord?.status;
  if (status === 422 && Array.isArray(detail)) return `${wat}: ${validatieInTaal(detail)}`;
  if (status === 403) return `${wat}: dat mag je met dit account niet.`;
  if (status === 409) return `${wat}: de les is intussen veranderd.`;
  if (status === 429) {
    return `${wat}: de daglimiet voor lessen is bereikt. Probeer het morgen opnieuw.`;
  }
  if (status === 422) return `${wat}: de server begreep de les niet. Schrijf hem iets anders.`;
  if (status === 400) return `${wat}: de server begreep de les niet. Schrijf hem iets uitgebreider.`;
  if (!antwoord) return `${wat}: de server is niet bereikbaar. Probeer het zo opnieuw.`;
  return `${wat}: er ging iets mis op de server. Probeer het zo opnieuw.`;
}

/**
 * Een 422 van FastAPI heeft een lijst `detail` met technische regels
 * ("String should have at most 2000 characters"). Die tonen we nooit
 * letterlijk: we zeggen in gewone taal wat er mis is.
 */
function validatieInTaal(detail: unknown[]): string {
  const regels = detail as { type?: unknown; loc?: unknown }[];
  const soorten = regels.map((r) => (typeof r?.type === "string" ? r.type : ""));
  const velden = regels.map((r) => (Array.isArray(r?.loc) ? r.loc.map(String) : []));
  if (soorten.some((t) => t.includes("too_long"))) {
    return `de les is te lang. Maximaal ${MAX_LES_TEKENS} tekens.`;
  }
  if (soorten.some((t) => t.includes("too_short") || t === "missing")) {
    return "er staat geen les in. Schrijf achter 'leer dit' wat Nova en Sanne moeten weten.";
  }
  if (velden.some((l) => l.includes("context"))) {
    return "de pagina of zaak waar je op staat werd niet herkend. Probeer het vanaf een andere pagina.";
  }
  return "de server begreep de les niet. Schrijf hem iets anders.";
}

/** "EUR 0,04". Onder een halve cent: "minder dan EUR 0,01". */
export function lesEuro(bedrag: number): string {
  if (bedrag > 0 && bedrag < 0.005) return "minder dan EUR 0,01";
  return `EUR ${bedrag.toFixed(2).replace(".", ",")}`;
}
